/**
 * Empty state for the chat panel — shown when no messages exist yet.
 *
 * HANDOFF_AI_CHAT_FEATURE_V1_REVISED §10. Three deal-aware prompt chips that
 * send the prompt immediately on click (one tap to a first answer).
 *
 * 2026-07-06 retune: each profile's set now leads with the shipped chat
 * tools (cost of credit, PG risk, component drill-down, covenant
 * explanation, repayment shape) so the first tap demonstrates a tool, not
 * just prose. Benchmark prompts stay only on rate_first — they are
 * Pro-gated and should not be a first-tap paywall for everyone.
 *
 * V1.1 Item 6: when the user has zero deals (and no active dealContext),
 * swap the generic chip set for a direct upload CTA so first-time users
 * have a clear path forward without needing to chat with the agent.
 *
 * HANDOFF_SCORING_PROFILE_PROPAGATION D5: headline / sub / prompts vary by
 * user.scoring_profile so the very first surface a user touches reflects
 * the priority they declared in onboarding.
 */
import { useNavigate } from 'react-router-dom';

import { useDeals } from '../../../api/hooks/useDeals';
import { useAuthStore } from '../../../stores/authStore';
import { useChatStore } from '../../../stores/chatStore';

interface Props {
  onPromptClick: (prompt: string) => void;
}

type ProfileKey = 'balanced' | 'cost_conscious' | 'growth_focused' | 'rate_first' | 'not_set';

const NO_DEALS_COPY: Record<ProfileKey, { headline: string; sub: string }> = {
  balanced:       { headline: 'Upload your term sheet to get started',         sub: "We'll score your offer and explain what every clause means." },
  cost_conscious: { headline: 'See exactly what your loan will cost',          sub: "Upload your term sheet — we'll break down every fee and show how it compares to the market." },
  growth_focused: { headline: "Know what's in the fine print before you sign", sub: "Upload your term sheet — we'll flag the covenants that could limit your growth plans." },
  rate_first:     { headline: 'Compare your rate against the market',          sub: "Upload your term sheet — we'll benchmark your margin against comparable Belgian deals." },
  not_set:        { headline: 'Upload a term sheet to get started',            sub: 'Ask me anything related to your financing deal.' },
};

const DEAL_CONTEXT_PROMPTS: Record<ProfileKey, string[]> = {
  rate_first:     ['How does my margin compare to the market?', 'What is the current EURIBOR 3M?',           'What does this loan actually cost me?'],
  cost_conscious: ['What does this loan actually cost me?',     'Explain my fees in detail',                 'What can I push back on?'],
  growth_focused: ['Which covenants should I review?',          'What does my leverage covenant actually mean?', 'What am I personally on the hook for?'],
  balanced:       ['What is my score on this deal?',            'What does this loan actually cost me?',     'What am I personally on the hook for?'],
  not_set:        ['Show me my deals',                          'What does this loan actually cost me?',     'How do I actually pay this back?'],
};

export function ChatEmptyState({ onPromptClick }: Props) {
  const navigate = useNavigate();
  const dealContext = useChatStore((s) => s.dealContext);
  const { data: dealsData, isLoading: isLoadingDeals } = useDeals();
  const profile: ProfileKey =
    (useAuthStore((s) => s.user)?.scoring_profile as ProfileKey | undefined) ?? 'not_set';

  const hasNoDeals =
    !isLoadingDeals && (dealsData?.deals?.length ?? 0) === 0 && !dealContext;

  if (hasNoDeals) {
    const noCopy = NO_DEALS_COPY[profile] ?? NO_DEALS_COPY.not_set;
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 p-6">
        <div className="text-center">
          <div className="flex items-center justify-center gap-3">
            <span aria-hidden className="animate-credia-pulse inline-block h-5 w-5 rounded-full bg-orange" />
            <p className="text-h1 font-bold tracking-tight text-txt-primary">Credia</p>
          </div>
          <p className="mt-2 text-body-sm text-txt-primary">{noCopy.headline}</p>
          <p className="mt-1 text-body-sm text-txt-secondary">{noCopy.sub}</p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/upload')}
          className="self-stretch border border-orange bg-surface-card px-3 py-2 text-left text-body-sm text-orange transition-colors hover:bg-orange hover:text-surface-card"
        >
          → Upload your first term sheet
        </button>
      </div>
    );
  }

  const prompts = DEAL_CONTEXT_PROMPTS[profile] ?? DEAL_CONTEXT_PROMPTS.not_set;

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 p-6">
      <div className="text-center">
        <div className="flex items-center justify-center gap-3">
          <span aria-hidden className="animate-credia-pulse inline-block h-5 w-5 rounded-full bg-orange" />
          <p className="text-h1 font-bold tracking-tight text-txt-primary">Credia</p>
        </div>
        <p className="mt-2 text-body-sm text-txt-secondary">
          Ask me anything related to your financing deal.
        </p>
      </div>
      <div className="mt-2 flex flex-col gap-2 self-stretch">
        {prompts.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => onPromptClick(p)}
            className="border border-border bg-surface-card px-3 py-2 text-left text-body-sm text-txt-primary transition-colors hover:border-orange hover:text-orange"
          >
            {p}
          </button>
        ))}
      </div>
    </div>
  );
}
